import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import {
  PageContainer,
  CommonCss,
  Card,
  DialogGridArea,
} from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import DraggableDialog from 'components/common/DraggableDialog';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  BUDGET_CLASS,
  BUDGET_CLASS_OPTIONS,
  YN_OPTIONS,
  ensureBudgetRounds,
  saveBudgetRounds,
  nextRoundNo,
  nextAdjNo,
  setDeptFlag,
  createBudgetFromPrevYear,
  copyPrevAdjToStage,
  computeFinalAdjNo,
  syncStageAmounts,
  createRoundForClass,
  validateRoundCodes,
  getDeleteRoundBlockReason,
  syncRoundKindFields,
  normalizeRoundRow,
  nextPreSeq,
  isActiveModRow,
  commitRoundsForSave,
  refreshDeptStatusForStage,
  findRequestAdjStage,
  loadBudgetRounds,
} from 'budget/budgetRoundStore';
import { loadExpenseRequests } from 'budget/budgetExpenseRequestStore';
import {
  rebuildBalancesOnBudgetConfirm,
  clearConfirmBalancesOnUnconfirm,
} from 'budget/budgetBalanceStore';
import {
  PRIOR_YEAR_BASIS_OPTIONS,
  FINAL_BUDGET_MODE_OPTIONS,
  FINAL_BUDGET_MODE,
  getPriorYearBasis,
  setPriorYearBasis,
  getFinalBudgetMode,
  setFinalBudgetMode,
} from 'budget/budgetTenantSettingsStore';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  importBizPlanToExpenseRequest,
  loadBizPlanMock,
} from 'budget/bizPlanImportToExpense';
import { listBizPlans } from 'budget/bizPlanStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { confirmBudgetRound, cancelConfirmBudgetRound } from 'budget/budgetRoundApi';

const isDirtyMod = (row) => row?.mod === 'A' || row?.mod === 'U' || row?.mod === 'D';

const touchMod = (row) => {
  if (!row) return row;
  if (row.mod === 'A' || row.mod === 'D') return row;
  return { ...row, mod: 'U' };
};

const ROUND_GRID_ID = 'budget-round-main';
const ADJ_GRID_ID = 'budget-round-adj';
const DEPT_GRID_ID = 'budget-round-dept';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 180px;
  padding: 0;
  overflow: hidden;
`;

const GridBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const formatDts = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return String(iso).replace('T', ' ').slice(0, 19);
  }
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const BudgetRoundRegisterInner = () => {
  const roundGridRef = useRef(null);
  const adjGridRef = useRef(null);
  const deptGridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = String(new Date().getFullYear());
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions();

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      fiscalYear: year,
      priorYearBasis: getPriorYearBasis(tenantCode),
      finalBudgetMode: getFinalBudgetMode(tenantCode),
    },
  });
  const { getValues, setValue } = methods;

  const [rounds, setRounds] = useState([]);
  const [selectedRoundUid, setSelectedRoundUid] = useState('');
  const [selectedAdjNo, setSelectedAdjNo] = useState(null);
  const [deptPopupOpen, setDeptPopupOpen] = useState(false);
  /** 팝업 오픈 직후 HiGrid 마운트 전 setData 유실 방지 */
  const pendingDeptRowsRef = useRef(null);
  const skipDeptGridSyncRef = useRef(false);
  const roundsRef = useRef(rounds);
  const selectedRoundUidRef = useRef(selectedRoundUid);
  roundsRef.current = rounds;
  selectedRoundUidRef.current = selectedRoundUid;

  /** 그리드 표시용 행 매핑 (React state → HiGrid setData는 명시적 호출만) */
  const toRoundGridRows = useCallback(
    (list) =>
      (list || []).map((r) => ({
        ...r,
        budgetConfirmAtDisp: formatDts(r.budgetConfirmAt),
      })),
    []
  );
  const toAdjGridRows = useCallback(
    (stages) =>
      (stages || []).map((s) => ({
        ...s,
        adjConfirmAtDisp: formatDts(s.adjConfirmAt),
      })),
    []
  );
  const pushRoundGrid = useCallback(
    (list) => {
      roundGridRef.current?.setData?.(toRoundGridRows(list));
    },
    [toRoundGridRows]
  );
  const pushAdjGrid = useCallback(
    (stages) => {
      adjGridRef.current?.setData?.(toAdjGridRows(stages));
    },
    [toAdjGridRows]
  );
  const pushAdjGridForList = useCallback(
    (list, uid) => {
      const round = (list || []).find((r) => r.roundUid === uid);
      pushAdjGrid(round?.adjStages || []);
    },
    [pushAdjGrid]
  );

  const selectedRound = useMemo(
    () => rounds.find((r) => r.roundUid === selectedRoundUid) || null,
    [rounds, selectedRoundUid]
  );
  const selectedStage = useMemo(() => {
    if (!selectedRound) return null;
    return (selectedRound.adjStages || []).find((s) => Number(s.adjNo) === Number(selectedAdjNo)) || null;
  }, [selectedRound, selectedAdjNo]);

  const roundLocked = selectedRound?.budgetConfirmYn === 'Y';
  const adjLocked = selectedStage?.adjConfirmYn === 'Y' || roundLocked;

  const refreshFromStore = useCallback(async () => {
    const tenant = tenantCode;
    const fy = getValues('fiscalYear') || year;
    const list = (await ensureBudgetRounds(tenant, fy)).map((r) => {
      const n = normalizeRoundRow(r, fy);
      return {
        ...n,
        finalAdjNo: computeFinalAdjNo(n.adjStages),
        adjStages: (n.adjStages || []).map(syncStageAmounts),
      };
    });
    setRounds(list);
    const nextUid =
      selectedRoundUidRef.current && list.some((r) => r.roundUid === selectedRoundUidRef.current)
        ? selectedRoundUidRef.current
        : list[0]?.roundUid || '';
    setSelectedRoundUid(nextUid);
    // 조회/로드 시에만 그리드에 주입 (편집 중 에코 setData 금지)
    pushRoundGrid(list);
    pushAdjGridForList(list, nextUid);
    return list;
  }, [getValues, year, tenantCode, pushRoundGrid, pushAdjGridForList]);

  useEffect(() => {
    refreshFromStore().catch((e) => {
      showAlert(e?.message || '예산차수 조회에 실패했습니다.', 'error');
    });
  }, [refreshFromStore]);

  const normalizeRoundsInMemory = useCallback((next) => {
    const list = next || [];
    return list.map((r) =>
      syncRoundKindFields(
        {
          ...r,
          finalAdjNo: computeFinalAdjNo((r.adjStages || []).filter(isActiveModRow)),
          adjStages: (r.adjStages || []).map(syncStageAmounts),
        },
        list
      )
    );
  }, []);

  /**
   * 화면 상태 갱신.
   * syncRoundGrid/syncAdjGrid: 행추가·삭제·조회 등 구조 변경 시에만 true (셀 편집 중 setData 금지)
   */
  const applyRoundsLocal = useCallback(
    (next, { syncRoundGrid = true, syncAdjGrid = false } = {}) => {
      const withKinds = normalizeRoundsInMemory(next);
      setRounds(withKinds);
      if (syncRoundGrid) pushRoundGrid(withKinds);
      if (syncAdjGrid) pushAdjGridForList(withKinds, selectedRoundUidRef.current);
      return withKinds;
    },
    [normalizeRoundsInMemory, pushRoundGrid, pushAdjGridForList]
  );

  const persistRounds = useCallback(
    async (next) => {
      const tenant = tenantCode;
      const fy = getValues('fiscalYear') || year;
      // 편성요구 제출 등이 저장소에만 반영된 경우, 화면 메모리에 deptRows가 비어 있으면
      // F10 저장이 제출상태를 덮어쓰지 않도록 저장소 deptRows를 병합한다.
      const lsRounds = await loadBudgetRounds(tenant, fy);
      const lsByUid = new Map((lsRounds || []).map((r) => [r.roundUid, r]));
      const mergedForSave = (next || []).map((r) => {
        const ls = lsByUid.get(r.roundUid);
        if (!ls) return r;
        const lsStageByNo = new Map(
          (ls.adjStages || []).map((s) => [Number(s.adjNo), s])
        );
        return {
          ...r,
          adjStages: (r.adjStages || []).map((s) => {
            const lsS = lsStageByNo.get(Number(s.adjNo));
            const memRows = s.deptRows || [];
            const lsRows = lsS?.deptRows || [];
            if (memRows.length > 0) return s;
            if (!lsRows.length) return s;
            return { ...s, deptRows: lsRows };
          }),
        };
      });
      const withKinds = normalizeRoundsInMemory(mergedForSave);
      const saved = await saveBudgetRounds(tenant, fy, withKinds);
      setRounds(saved);
      pushRoundGrid(saved);
      pushAdjGridForList(saved, selectedRoundUidRef.current);
      return saved;
    },
    [getValues, year, tenantCode, normalizeRoundsInMemory, pushRoundGrid, pushAdjGridForList]
  );

  /**
   * 선택 차수 갱신.
   * @param {{ persist?: boolean, syncAdjGrid?: boolean }} opts
   */
  const updateSelectedRound = useCallback(
    async (updater, opts = {}) => {
      if (!selectedRoundUid) return;
      const { persist = false, syncAdjGrid = true } = opts;
      const next = roundsRef.current.map((r) => {
        if (r.roundUid !== selectedRoundUid) return r;
        return typeof updater === 'function' ? updater(r) : updater;
      });
      if (persist) await persistRounds(next);
      else applyRoundsLocal(next, { syncRoundGrid: false, syncAdjGrid });
    },
    [selectedRoundUid, persistRounds, applyRoundsLocal]
  );

  const adjStages = useMemo(() => {
    const stages = selectedRound?.adjStages || [];
    return stages
      .map(syncStageAmounts)
      .sort((a, b) => Number(a.adjNo) - Number(b.adjNo));
  }, [selectedRound]);

  useEffect(() => {
    if (!adjStages.length) {
      setSelectedAdjNo(null);
      return;
    }
    if (!adjStages.some((s) => Number(s.adjNo) === Number(selectedAdjNo))) {
      setSelectedAdjNo(Number(adjStages[0].adjNo));
    }
  }, [adjStages, selectedAdjNo]);

  const roundColumns = useMemo(() => {
    const classSeqCol = hiCol.edNumberColumn({
      header: '차수',
      field: 'classSeq',
      size: 70,
      align: 'center',
    });
    const preSeqCol = hiCol.edNumberColumn({
      header: '성립전',
      field: 'preSeq',
      size: 70,
      align: 'center',
      title: '0=추경 본편, 1~9=성립전 (결합코드 끝자리)',
    });
    const applyDateCol = hiCol.edDateColumn({
      header: '적용일자',
      field: 'applyDate',
      size: 110,
      align: 'center',
    });
    return [
      hiCol.edComboColumn({
        header: '예산구분',
        field: 'budgetClass',
        size: 110,
        options: BUDGET_CLASS_OPTIONS,
        required: true,
      }),
      {
        ...classSeqCol,
        meta: {
          ...classSeqCol.meta,
          editable: (row) => row?.budgetClass === BUDGET_CLASS.SUPP || row?.budgetClass === BUDGET_CLASS.DEEMED,
        },
      },
      {
        ...preSeqCol,
        meta: {
          ...preSeqCol.meta,
          editable: (row) => row?.budgetClass === BUDGET_CLASS.SUPP,
        },
      },
      hiCol.roTextColumn({
        header: '결합코드',
        field: 'budgetKindCode',
        size: 90,
        align: 'center',
        title: '100 / 210·211·212 / 220 / 301…',
      }),
      hiCol.edTextColumn({ header: '예산차수명', field: 'roundName', size: 150, required: true }),
      hiCol.edComboColumn({
        header: '사업계획',
        field: 'planUseYn',
        size: 90,
        options: YN_OPTIONS,
        title: 'Y이면 사업계획등록 후 편성, N이면 편성등록 직행',
      }),
      {
        ...applyDateCol,
        meta: {
          ...applyDateCol.meta,
          editable: (row) => row?.budgetConfirmYn !== 'Y',
        },
      },
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 120 }),
      hiCol.roTextColumn({ header: '최종조정', field: 'finalAdjNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '예산확정', field: 'budgetConfirmYn', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '확정일시', field: 'budgetConfirmAtDisp', size: 140 }),
      hiCol.roTextColumn({ header: '확정자', field: 'budgetConfirmBy', size: 90 }),
    ];
  }, []);

  const adjColumns = useMemo(() => {
    const inputDeadlineCol = hiCol.edDateColumn({
      header: '입력마감일',
      field: 'inputDeadline',
      size: 120,
      align: 'center',
    });
    const roundConfirmed = selectedRound?.budgetConfirmYn === 'Y';
    return [
      hiCol.edNumberColumn({ header: '차수', field: 'adjNo', size: 70, align: 'center' }),
      hiCol.edTextColumn({ header: '조정차수명', field: 'adjName', size: 140, required: true }),
      {
        ...inputDeadlineCol,
        meta: {
          ...inputDeadlineCol.meta,
          editable: (row) => !roundConfirmed && row?.adjConfirmYn !== 'Y',
        },
      },
      hiCol.roNumberColumn({ header: '조정전금액', field: 'amtBefore', size: 120, align: 'right' }),
      hiCol.roNumberColumn({ header: '조정후금액', field: 'amtAfter', size: 120, align: 'right' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
      hiCol.roTextColumn({ header: '조정확정', field: 'adjConfirmYn', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '확정일시', field: 'adjConfirmAtDisp', size: 150 }),
      hiCol.roTextColumn({ header: '확정자', field: 'adjConfirmBy', size: 100 }),
    ];
  }, [selectedRound?.budgetConfirmYn]);

  const deptColumns = useMemo(
    () => [
      hiCol.roTextColumn({
        header: '수지',
        field: 'inoutDiv',
        size: 80,
        align: 'center',
      }),
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
      hiCol.roTextColumn({ header: '예산부서', field: 'deptName', size: 120 }),
      hiCol.roNumberColumn({
        header: '조정전금액',
        field: 'amtBefore',
        size: 120,
        align: 'right',
        footer: { option: 'sum' },
      }),
      hiCol.roNumberColumn({
        header: '조정후금액',
        field: 'amtAfter',
        size: 120,
        align: 'right',
        footer: { option: 'sum' },
      }),
      hiCol.roTextColumn({ header: '제출', field: 'submitYn', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '제출일시', field: 'submitAtDisp', size: 160 }),
      hiCol.roTextColumn({ header: '제출자', field: 'submitBy', size: 90 }),
      hiCol.roTextColumn({ header: '접수', field: 'receiveYn', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '접수일시', field: 'receiveAtDisp', size: 160 }),
      hiCol.roTextColumn({ header: '접수자', field: 'receiveBy', size: 90 }),
      hiCol.roTextColumn({ header: '조정', field: 'adjustYn', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '조정일시', field: 'adjustAtDisp', size: 160 }),
      hiCol.roTextColumn({ header: '조정자', field: 'adjustBy', size: 90 }),
    ],
    []
  );

  const deptGridRows = useMemo(
    () =>
      (selectedStage?.deptRows || []).map((d) => ({
        ...d,
        submitAtDisp: formatDts(d.submitAt),
        receiveAtDisp: formatDts(d.receiveAt),
        adjustAtDisp: formatDts(d.adjustAt),
      })),
    [selectedStage]
  );

  const roundGrid = useHiGridHook({
    gridRef: roundGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: createRoundForClass(year, BUDGET_CLASS.SUPP, []).row,
  });

  const adjGrid = useHiGridHook({
    gridRef: adjGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      adjNo: 1,
      adjName: '',
      inputDeadline: '',
      amtBefore: 0,
      amtAfter: 0,
      remark: '',
      adjConfirmYn: 'N',
    },
  });

  // rounds 변경 시 자동 setData 금지 — 셀 편집 중 입력값 덮어쓰기 방지.
  // 그리드 주입은 refreshFromStore / applyRoundsLocal / persistRounds / 차수 선택 시에만.

  // 팝업 오픈 시 pending 행 주입 (HiGrid 마운트 대기)
  useEffect(() => {
    if (!deptPopupOpen) return undefined;
    const rows = pendingDeptRowsRef.current;
    if (!rows) return undefined;
    pendingDeptRowsRef.current = null;
    const t = window.setTimeout(() => {
      deptGridRef.current?.setData?.(rows);
      skipDeptGridSyncRef.current = false;
    }, 50);
    return () => window.clearTimeout(t);
  }, [deptPopupOpen]);

  // 팝업이 열린 뒤 스토어/플래그 변경 반영 (오픈 직후 pending 주입 중에는 스킵)
  useEffect(() => {
    if (!deptPopupOpen || skipDeptGridSyncRef.current) return;
    deptGridRef.current?.setData?.(deptGridRows);
  }, [deptGridRows, deptPopupOpen]);

  // 예산차수 선택 변경 시에만 하단 조정차수 그리드 교체
  useEffect(() => {
    const list = roundsRef.current;
    const round = list.find((r) => r.roundUid === selectedRoundUid);
    pushAdjGrid(round?.adjStages || []);
  }, [selectedRoundUid, pushAdjGrid]);

  useEffect(() => {
    roundGrid.onRowClicked = ({ data }) => {
      if (data?.roundUid) setSelectedRoundUid(data.roundUid);
    };
    roundGrid.onSelectRow = (rowData) => {
      if (rowData?.roundUid) setSelectedRoundUid(rowData.roundUid);
    };
    // 셀 저장 → React 상태만 갱신 (그리드 setData 에코 없음)
    roundGrid.onAfterSaveCell = (_rowData, cellInfo) => {
      const field = cellInfo?.field;
      const gridData = roundGridRef.current?.getData?.() || [];
      const needsKindSync = field === 'budgetClass' || field === 'classSeq' || field === 'preSeq';
      const synced = needsKindSync
        ? gridData.map((g) => syncRoundKindFields(g, gridData))
        : gridData;
      if (needsKindSync) {
        // 결합코드 표시만 그리드에 재주입 (편집은 이미 종료된 시점)
        pushRoundGrid(
          synced.map((g) => {
            const prev = roundsRef.current.find((r) => r.roundUid === g.roundUid) || {};
            return {
              ...prev,
              ...g,
              mod: g.mod || prev.mod,
              adjStages: prev.adjStages || g.adjStages || [],
            };
          })
        );
      }
      setRounds((prev) => {
        const byUid = new Map(prev.map((r) => [r.roundUid, r]));
        return synced.map((g) => {
          const prevRow = byUid.get(g.roundUid) || {};
          return {
            ...prevRow,
            ...g,
            mod: g.mod || prevRow.mod,
            adjStages: prevRow.adjStages || g.adjStages || [],
          };
        });
      });
    };
  }, [roundGrid, pushRoundGrid]);

  const handleAdjRowOpenDeptPopup = useCallback((rowData) => {
    // 요구/조정 차수 행이 있을 때만 팝업 (빈 영역·헤더 더블클릭 제외)
    if (rowData?.adjNo == null) return;
    // openDeptPopup이 선택 adjNo 기준으로 집계 조회함
    setSelectedAdjNo(Number(rowData.adjNo));
    // 선택 반영 후 조회 오픈 (동일 틱에서 selectedAdjNo가 아직 옛값일 수 있어 adjNo 인자로 전달)
    window.setTimeout(() => openDeptPopupRef.current?.(Number(rowData.adjNo)), 0);
  }, []);

  const openDeptPopup = useCallback(
    async (forceAdjNo) => {
      if (!selectedRound) {
        showAlert('상단에서 예산차수를 선택하세요.', 'warning');
        return;
      }
      const fy = getValues('fiscalYear') || year;
      const kind = selectedRound.budgetKindCode;
      if (!kind) {
        showAlert('결합코드가 없는 차수입니다. 예산구분을 확인하세요.', 'warning');
        return;
      }

      // 툴바: 선택 단계 없으면 「요구」단계 우선. 더블클릭은 forceAdjNo로 해당 단계.
      const reqStage = findRequestAdjStage(selectedRound.adjStages);
      let adjNo =
        forceAdjNo != null
          ? Number(forceAdjNo)
          : selectedAdjNo != null
            ? Number(selectedAdjNo)
            : null;
      if (adjNo == null && reqStage) {
        adjNo = Number(reqStage.adjNo);
      }

      if (adjNo == null) {
        showAlert(
          '하단에서 조정차수(부서요구 등)를 추가·선택한 뒤 다시 열어주세요.',
          'warning'
        );
        return;
      }

      // 제출 상태는 「요구」단계에만 반영됨 — 다른 단계면 안내
      const targetStage = (selectedRound.adjStages || []).find(
        (s) => Number(s.adjNo) === Number(adjNo)
      );
      const isRequestStage = targetStage && String(targetStage.adjName || '').includes('요구');
      if (forceAdjNo == null && reqStage && !isRequestStage) {
        adjNo = Number(reqStage.adjNo);
      }

      setSelectedAdjNo(adjNo);

      try {
        const expenseRows = await loadExpenseRequests({
          tenantCode,
          fiscalYear: fy,
          budgetKindCode: kind,
        });
        const refreshed = await refreshDeptStatusForStage(
          tenantCode,
          fy,
          kind,
          adjNo,
          expenseRows
        );
        if (!refreshed.ok) {
          showAlert(refreshed.reason || '부서 현황을 조회할 수 없습니다.', 'warning');
          return;
        }
        const list = await refreshFromStore();
        const round =
          list.find((r) => r.roundUid === selectedRound.roundUid) ||
          list.find((r) => String(r.budgetKindCode) === String(kind));
        const stage =
          (round?.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo)) || null;
        const rows = (stage?.deptRows || refreshed.rows || []).map((d) => ({
          ...d,
          submitAtDisp: formatDts(d.submitAt),
          receiveAtDisp: formatDts(d.receiveAt),
          adjustAtDisp: formatDts(d.adjustAt),
        }));
        if (!rows.length) {
          showAlert(
            '표시할 부서가 없습니다. 부서마스터(사용중) 또는 편성요구 데이터를 확인하세요.',
            'warning'
          );
        }
        pendingDeptRowsRef.current = rows;
        skipDeptGridSyncRef.current = true;
        setDeptPopupOpen(true);
      } catch (e) {
        showAlert(e?.message || '부서 현황 조회에 실패했습니다.', 'error');
      }
    },
    [
      selectedRound,
      selectedAdjNo,
      getValues,
      year,
      tenantCode,
      refreshFromStore,
    ]
  );

  const openDeptPopupRef = useRef(openDeptPopup);
  openDeptPopupRef.current = openDeptPopup;

  useEffect(() => {
    adjGrid.onRowClicked = ({ data }) => {
      if (data?.adjNo != null) setSelectedAdjNo(Number(data.adjNo));
    };
    adjGrid.onSelectRow = (rowData) => {
      if (rowData?.adjNo != null) setSelectedAdjNo(Number(rowData.adjNo));
    };
    adjGrid.onGridCellDoubleClicked = (rowData) => {
      handleAdjRowOpenDeptPopup(rowData);
    };
    adjGrid.onAfterSaveCell = (rowData) => {
      const uid = selectedRoundUidRef.current;
      if (!uid) return;
      const gridData = adjGridRef.current?.getData?.() || [];
      const editedAdjNo = rowData?.adjNo != null ? Number(rowData.adjNo) : null;
      setRounds((prev) =>
        prev.map((r) => {
          if (r.roundUid !== uid) return r;
          const byNo = new Map((r.adjStages || []).map((s) => [Number(s.adjNo), s]));
          let editedBecameDirty = false;
          const stages = gridData.map((g) => {
            const prevS = byNo.get(Number(g.adjNo)) || {};
            const nextMod = g.mod || prevS.mod;
            if (editedAdjNo != null && Number(g.adjNo) === editedAdjNo && isDirtyMod({ mod: nextMod })) {
              editedBecameDirty = true;
            }
            return syncStageAmounts({
              ...prevS,
              ...g,
              mod: nextMod,
              deptRows: prevS.deptRows || g.deptRows || [],
              adjConfirmYn: prevS.adjConfirmYn || g.adjConfirmYn || 'N',
              adjConfirmAt: prevS.adjConfirmAt ?? g.adjConfirmAt,
              adjConfirmBy: prevS.adjConfirmBy ?? g.adjConfirmBy,
            });
          });
          const next = { ...r, adjStages: stages };
          return editedBecameDirty ? touchMod(next) : next;
        })
      );
    };
  }, [adjGrid, handleAdjRowOpenDeptPopup]);

  const syncRoundGridEdits = useCallback(() => {
    const gridData = roundGridRef.current?.getData?.() || [];
    const byUid = new Map(rounds.map((r) => [r.roundUid, r]));
    const merged = gridData.map((g) => {
      const prev = byUid.get(g.roundUid) || {};
      return {
        ...prev,
        ...g,
        mod: g.mod || prev.mod,
        adjStages: prev.adjStages || g.adjStages || [],
        budgetConfirmAt: prev.budgetConfirmAt,
        budgetConfirmBy: prev.budgetConfirmBy,
        budgetConfirmYn: prev.budgetConfirmYn || g.budgetConfirmYn || 'N',
      };
    });
    return merged.map((r) => syncRoundKindFields(r, merged));
  }, [rounds]);

  const syncAdjGridEdits = useCallback(() => {
    if (!selectedRound) return null;
    const gridData = adjGridRef.current?.getData?.() || [];
    const byNo = new Map((selectedRound.adjStages || []).map((s) => [Number(s.adjNo), s]));
    const stages = gridData.map((g) => {
      const prev = byNo.get(Number(g.adjNo)) || {};
      return syncStageAmounts({
        ...prev,
        ...g,
        mod: g.mod || prev.mod,
        deptRows: prev.deptRows || g.deptRows || [],
        adjConfirmYn: prev.adjConfirmYn || g.adjConfirmYn || 'N',
        adjConfirmAt: prev.adjConfirmAt ?? g.adjConfirmAt,
        adjConfirmBy: prev.adjConfirmBy ?? g.adjConfirmBy,
      });
    });
    return { ...selectedRound, adjStages: stages };
  }, [selectedRound]);

  const handleSearch = useCallback(async () => {
    try {
      await refreshFromStore();
      showAlert('예산차수를 조회했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [refreshFromStore]);

  /** 사업계획서(IndexedDB 최신 건) → 선택 차수의 지출예산편성요구 insert */
  const handleImportBizPlan = useCallback(async () => {
    if (!selectedRound) {
      showAlert('예산차수를 먼저 선택하세요.', 'warning');
      return;
    }
    const fy = String(getValues('fiscalYear') || year).trim();
    let plans = [];
    try {
      plans = await listBizPlans({ tenantCode, fiscalYear: fy });
    } catch (e) {
      console.warn(e);
    }
    const latest = plans[0] || null;
    const mock = latest || loadBizPlanMock(tenantCode);
    if (!mock) {
      showAlert(
        '저장된 사업계획서가 없습니다.\n사업계획 > 사업계획서 작성에서 「저장」 후 다시 시도하세요.',
        'warning',
      );
      return;
    }

    const deptLabel = mock.picked?.deptName || mock.fields?.['header.deptName'] || '(부서 미지정)';
    const projectLabel =
      mock.title || mock.picked?.projectName || mock.fields?.['doc.title'] || '(사업 미지정)';
    const extraLines = [
      `선택 차수: ${selectedRound.roundName || ''} (${selectedRound.budgetKindCode || ''})`,
      `사업계획: ${projectLabel} / ${deptLabel}`,
      latest
        ? `계획ID: ${latest.planId} · ${latest.updatedAt || '—'}`
        : `저장시각: ${mock.savedAt || '—'} (구 mock)`,
    ];
    if (plans.length > 1) {
      extraLines.push(`※ 당해 연도 ${plans.length}건 중 최신 1건을 가져옵니다.`);
    }
    extraLines.push('', '지출예산편성요구에 1건을 추가할까요?');
    const ok = await showConfirm(extraLines.join('\n'), 'info');
    if (!ok) return;

    let result;
    try {
      result = await importBizPlanToExpenseRequest({
        tenantCode,
        fiscalYear: fy,
        round: selectedRound,
        planId: latest?.planId,
        replaceSameSource: false,
      });
      if (!result.ok) {
        showAlert(result.reason || '가져오기에 실패했습니다.', 'warning');
        return;
      }
      await refreshFromStore();
    } catch (e) {
      showAlert(e?.message || '가져오기에 실패했습니다.', 'error');
      return;
    }

    const tips = [
      `지출예산편성요구에 반영했습니다. (${result.budgetKindCode})`,
      `요구단계: ${result.reqStageName || '—'}`,
      `부서 ${result.row?.deptName || '—'} · 사업 ${result.row?.projectName || '—'}`,
      `예산액(천원) ${Number(result.row?.budgetAmt || 0).toLocaleString()}`,
    ];
    if (result.duplicateHint) {
      tips.push('※ 동일 부서·사업·과목 행이 이미 있어 추가 1건이 더 생겼습니다.');
    }
    if (!result.fieldsExtracted) {
      tips.push(
        '※ 필드가 비어 있습니다. 사업계획서 작성에서 누름틀·편성연동 입력 후 다시 저장하세요.',
      );
    }
    tips.push('메뉴: 예산편성 > 지출예산편성요구 에서 확인하세요.');
    showAlert(tips.join('\n'), 'success');
  }, [selectedRound, getValues, year, tenantCode, refreshFromStore]);

  const handleSave = useCallback(async () => {
    const mergedRounds = syncRoundGridEdits();
    const adjSynced = syncAdjGridEdits();
    let next = mergedRounds;
    if (adjSynced) {
      next = mergedRounds.map((r) => {
        if (r.roundUid !== adjSynced.roundUid) return r;
        const linked = syncRoundKindFields(adjSynced, mergedRounds);
        const stageDirty = (linked.adjStages || []).some(isDirtyMod);
        if (stageDirty && linked.mod !== 'A' && linked.mod !== 'D') {
          return { ...linked, mod: 'U' };
        }
        return linked;
      });
    }
    const dirty = next.some(
      (r) => isDirtyMod(r) || (r.adjStages || []).some(isDirtyMod)
    );
    if (!dirty) {
      showAlert('저장할 데이터가 없습니다.', 'warning');
      return;
    }
    const committed = commitRoundsForSave(next);
    const validation = validateRoundCodes(committed);
    if (!validation.ok) {
      showAlert(validation.errors.join('\n'), 'warning');
      return;
    }
    const ok = await showConfirm('저장하시겠습니까?', 'success');
    if (!ok) return;
    try {
      await persistRounds(committed);
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    if (validation.warnings.length) {
      showAlert(`저장되었습니다.\n${validation.warnings.join('\n')}`, 'info');
    } else {
      showAlert('저장되었습니다.', 'success');
    }
  }, [syncRoundGridEdits, syncAdjGridEdits, persistRounds]);

  const handleAddRound = useCallback(() => {
    const fy = getValues('fiscalYear') || year;
    const base = syncRoundGridEdits();
    const hasMain = base.some((r) => isActiveModRow(r) && r.budgetClass === BUDGET_CLASS.MAIN);
    const defaultClass = hasMain ? BUDGET_CLASS.SUPP : BUDGET_CLASS.MAIN;
    const created = createRoundForClass(fy, defaultClass, base, {
      roundNo: nextRoundNo(base),
      preSeq: 0,
    });
    if (!created.ok) {
      showAlert(created.reason, 'warning');
      return;
    }
    const next = [...base, { ...created.row, mod: 'A' }];
    applyRoundsLocal(next);
    setSelectedRoundUid(created.row.roundUid);
  }, [getValues, year, syncRoundGridEdits, applyRoundsLocal]);

  /** 선택 추경 차수에 성립전 행 추가 (211, 212…) */
  const handleAddPreRound = useCallback(() => {
    const fy = getValues('fiscalYear') || year;
    const base = syncRoundGridEdits();
    const activeSupp = base.filter(
      (r) => isActiveModRow(r) && r.budgetClass === BUDGET_CLASS.SUPP
    );
    const anchor =
      selectedRound?.budgetClass === BUDGET_CLASS.SUPP && isActiveModRow(selectedRound)
        ? selectedRound
        : activeSupp.slice(-1)[0];
    if (!anchor) {
      showAlert('성립전을 붙일 추경예산을 먼저 선택(또는 등록)하세요.', 'warning');
      return;
    }
    const classSeq = Number(anchor.classSeq) || 1;
    const preSeq = nextPreSeq(base, classSeq);
    if (preSeq == null) {
      showAlert('해당 추경 차수의 성립전은 최대 9회까지입니다.', 'warning');
      return;
    }
    const created = createRoundForClass(fy, BUDGET_CLASS.SUPP, base, {
      roundNo: nextRoundNo(base),
      classSeq,
      preSeq,
    });
    if (!created.ok) {
      showAlert(created.reason, 'warning');
      return;
    }
    const next = [...base, { ...created.row, mod: 'A' }];
    applyRoundsLocal(next);
    setSelectedRoundUid(created.row.roundUid);
  }, [getValues, year, selectedRound, syncRoundGridEdits, applyRoundsLocal]);

  const handleDeleteRound = useCallback(async () => {
    if (!selectedRound) {
      showAlert('삭제할 예산차수를 선택하세요.', 'warning');
      return;
    }
    if (selectedRound.budgetConfirmYn === 'Y') {
      showAlert('확정된 예산차수는 삭제할 수 없습니다.', 'warning');
      return;
    }
    const current = syncRoundGridEdits();
    const target = current.find((r) => r.roundUid === selectedRoundUid);
    if (!target) {
      showAlert('삭제할 예산차수를 선택하세요.', 'warning');
      return;
    }
    if (target.mod === 'D') {
      showAlert('이미 삭제 표시된 행입니다. 저장하면 반영됩니다.', 'info');
      return;
    }
    const block = getDeleteRoundBlockReason(current, selectedRoundUid);
    if (block) {
      showAlert(block, 'warning');
      return;
    }
    const ok = await showConfirm(
      `「${selectedRound.roundName}」(${selectedRound.budgetKindCode}) 차수를 삭제할까요?`,
      'warning'
    );
    if (!ok) return;
    let next;
    if (target.mod === 'A') {
      next = current.filter((r) => r.roundUid !== selectedRoundUid);
    } else {
      next = current.map((r) =>
        r.roundUid === selectedRoundUid ? { ...r, mod: 'D' } : r
      );
    }
    applyRoundsLocal(next);
    const remain = next.filter(isActiveModRow);
    setSelectedRoundUid(remain[0]?.roundUid || '');
  }, [selectedRound, selectedRoundUid, syncRoundGridEdits, applyRoundsLocal]);

  const handleAddAdj = useCallback(() => {
    if (!selectedRound) {
      showAlert('상단에서 예산차수를 선택하세요.', 'warning');
      return;
    }
    if (roundLocked) {
      showAlert('예산확정된 차수에는 조정차수를 추가할 수 없습니다.', 'warning');
      return;
    }
    const stages = [...(syncAdjGridEdits()?.adjStages || selectedRound.adjStages || [])];
    const adjNo = nextAdjNo(stages);
    // 1차=부서요구, 이후=1차조정·2차조정…
    const adjName = adjNo === 1 ? '부서요구' : `${adjNo - 1}차조정`;
    stages.push({
      adjNo,
      adjName,
      inputDeadline: '',
      amtBefore: 0,
      amtAfter: 0,
      remark: '',
      adjConfirmYn: 'N',
      adjConfirmAt: null,
      adjConfirmBy: null,
      deptRows: [],
      mod: 'A',
    });
    updateSelectedRound(touchMod({ ...selectedRound, adjStages: stages }));
    setSelectedAdjNo(adjNo);
  }, [selectedRound, roundLocked, syncAdjGridEdits, updateSelectedRound]);

  const handleDeleteAdj = useCallback(async () => {
    if (!selectedRound || selectedAdjNo == null) {
      showAlert('삭제할 조정차수를 선택하세요.', 'warning');
      return;
    }
    if (adjLocked) {
      showAlert('확정된 조정차수(또는 예산확정)는 삭제할 수 없습니다.', 'warning');
      return;
    }
    const stages = [...(syncAdjGridEdits()?.adjStages || selectedRound.adjStages || [])];
    const target = stages.find((s) => Number(s.adjNo) === Number(selectedAdjNo));
    if (!target) {
      showAlert('삭제할 조정차수를 선택하세요.', 'warning');
      return;
    }
    if (target.mod === 'D') {
      showAlert('이미 삭제 표시된 행입니다. 저장하면 반영됩니다.', 'info');
      return;
    }
    const ok = await showConfirm(`조정차수 ${selectedAdjNo}를 삭제할까요?`, 'warning');
    if (!ok) return;
    let nextStages;
    if (target.mod === 'A') {
      nextStages = stages.filter((s) => Number(s.adjNo) !== Number(selectedAdjNo));
    } else {
      nextStages = stages.map((s) =>
        Number(s.adjNo) === Number(selectedAdjNo) ? { ...s, mod: 'D' } : s
      );
    }
    updateSelectedRound(touchMod({ ...selectedRound, adjStages: nextStages }));
    const remain = nextStages.filter(isActiveModRow);
    setSelectedAdjNo(remain[0]?.adjNo != null ? Number(remain[0].adjNo) : null);
  }, [selectedRound, selectedAdjNo, adjLocked, syncAdjGridEdits, updateSelectedRound]);

  const handleAdjConfirm = useCallback(async () => {
    if (!selectedRound || !selectedStage) {
      showAlert('조정차수를 선택하세요.', 'warning');
      return;
    }
    if (roundLocked) {
      showAlert('이미 예산확정된 차수입니다.', 'warning');
      return;
    }
    if (selectedStage.adjConfirmYn === 'Y') {
      showAlert('이미 조정확정된 차수입니다.', 'info');
      return;
    }
    const ok = await showConfirm(
      `「${selectedStage.adjName}」을(를) 조정확정 할까요?\n확정 후 해당 차수 부서 입력·상태변경이 잠깁니다.`,
      'warning'
    );
    if (!ok) return;
    const at = new Date().toISOString();
    const by = user?.id || user?.name || '';
    const stages = (selectedRound.adjStages || []).map((s) =>
      Number(s.adjNo) === Number(selectedAdjNo)
        ? { ...s, adjConfirmYn: 'Y', adjConfirmAt: at, adjConfirmBy: by }
        : s
    );
    try {
      await updateSelectedRound(
        { ...selectedRound, adjStages: stages, finalAdjNo: computeFinalAdjNo(stages) },
        { persist: true }
      );
    } catch (e) {
      showAlert(e?.message || '조정확정에 실패했습니다.', 'error');
      return;
    }
    showAlert('조정확정되었습니다.', 'success');
  }, [selectedRound, selectedStage, selectedAdjNo, roundLocked, user, updateSelectedRound]);

  const handleAdjUnconfirm = useCallback(async () => {
    if (!selectedRound || !selectedStage) return;
    if (roundLocked) {
      showAlert('예산확정된 차수는 조정확정을 해제할 수 없습니다. 먼저 예산확정 취소하세요.', 'warning');
      return;
    }
    if (selectedStage.adjConfirmYn !== 'Y') {
      showAlert('조정확정 상태가 아닙니다.', 'info');
      return;
    }
    const laterConfirmed = (selectedRound.adjStages || []).some(
      (s) => Number(s.adjNo) > Number(selectedAdjNo) && s.adjConfirmYn === 'Y'
    );
    if (laterConfirmed) {
      showAlert('이후 조정차수가 확정되어 있어 해제할 수 없습니다.', 'warning');
      return;
    }
    const ok = await showConfirm('조정확정을 취소할까요?', 'warning');
    if (!ok) return;
    const stages = (selectedRound.adjStages || []).map((s) =>
      Number(s.adjNo) === Number(selectedAdjNo)
        ? { ...s, adjConfirmYn: 'N', adjConfirmAt: null, adjConfirmBy: null }
        : s
    );
    try {
      await updateSelectedRound(
        { ...selectedRound, adjStages: stages, finalAdjNo: computeFinalAdjNo(stages) },
        { persist: true }
      );
    } catch (e) {
      showAlert(e?.message || '조정확정 취소에 실패했습니다.', 'error');
      return;
    }
    showAlert('조정확정이 취소되었습니다.', 'success');
  }, [selectedRound, selectedStage, selectedAdjNo, roundLocked, updateSelectedRound]);

  const handleBudgetConfirm = useCallback(async () => {
    if (!selectedRound) {
      showAlert('예산차수를 선택하세요.', 'warning');
      return;
    }
    if (selectedRound.budgetConfirmYn === 'Y') {
      showAlert('이미 예산확정된 차수입니다.', 'info');
      return;
    }
    const stages = selectedRound.adjStages || [];
    const anyConfirmed = stages.some((s) => s.adjConfirmYn === 'Y');
    if (!anyConfirmed) {
      showAlert('조정확정된 차수가 있어야 예산확정할 수 있습니다.', 'warning');
      return;
    }
    const ok = await showConfirm(
      `「${selectedRound.roundName}」을(를) 예산확정 할까요?\n확정 후 차수·조정 수정이 잠기고 현액에 반영됩니다.`,
      'warning'
    );
    if (!ok) return;
    const at = new Date().toISOString();
    const by = user?.id || user?.name || '';
    try {
      if (isBdApiEnabled()) {
        const res = await confirmBudgetRound(selectedRound.roundUid, {
          version: selectedRound.version,
          finalAdjNo: computeFinalAdjNo(stages),
        });
        await refreshFromStore();
        const count = res?.balanceCount ?? res?.count ?? 0;
        showAlert(
          `예산확정되었습니다.\n현액 ${count}건 반영 (지출 ${res?.expenseCount ?? 0} · 수입 ${res?.incomeCount ?? 0})`,
          'success'
        );
        return;
      }
      await updateSelectedRound(
        {
          ...selectedRound,
          budgetConfirmYn: 'Y',
          budgetConfirmAt: at,
          budgetConfirmBy: by,
          finalAdjNo: computeFinalAdjNo(stages),
        },
        { persist: true }
      );
    } catch (e) {
      showAlert(e?.message || '예산확정에 실패했습니다.', 'error');
      return;
    }
    // 현액 적재 — 실패해도 확정 자체는 유지 (화면에서 재적재 가능)
    let bal;
    try {
      bal = await rebuildBalancesOnBudgetConfirm({
        tenantCode,
        fiscalYear: getValues('fiscalYear') || year,
        budgetKindCode: selectedRound.budgetKindCode,
        confirmedAt: at,
        confirmedBy: by,
      });
    } catch (e) {
      bal = { ok: false, reason: e?.message || '현액 적재 오류' };
    }
    if (bal.ok) {
      if (bal.skipped) {
        showAlert(
          `예산확정되었습니다.\n${bal.skipReason || '예외 차수라 현액에 반영하지 않았습니다.'}`,
          'success'
        );
      } else {
        const modeLabel =
          bal.mode === FINAL_BUDGET_MODE.LAST_ROUND ? 'lastRound' : 'sumDelta';
        const absorbHint =
          bal.absorbedSeonglipCount > 0
            ? `\n성립전 ${bal.absorbedSeonglipCount}건 현액 → 본편 흡수`
            : '';
        showAlert(
          `예산확정되었습니다. [${modeLabel}]\n현액 ${bal.count}건 반영 (지출 ${bal.expenseCount} · 수입 ${bal.incomeCount})${absorbHint}`,
          'success'
        );
      }
    } else {
      showAlert(
        `예산확정되었습니다.\n현액 반영 실패: ${bal.reason || '-'}\n현액관리에서 「확정분 재적재」를 사용하세요.`,
        'warning'
      );
    }
  }, [selectedRound, user, updateSelectedRound, tenantCode, getValues, year, refreshFromStore]);

  const handleBudgetUnconfirm = useCallback(async () => {
    if (!selectedRound) return;
    if (selectedRound.budgetConfirmYn !== 'Y') {
      showAlert('예산확정 상태가 아닙니다.', 'info');
      return;
    }
    const ok = await showConfirm(
      '예산확정을 취소할까요?\n해당 차수의 편성확정 현액(당해)도 함께 제거됩니다.',
      'warning'
    );
    if (!ok) return;
    let cleared;
    try {
      if (isBdApiEnabled()) {
        const res = await cancelConfirmBudgetRound(selectedRound.roundUid, {
          version: selectedRound.version,
        });
        await refreshFromStore();
        showAlert(
          `예산확정이 취소되었습니다.${
            res?.removed ? `\n현액 ${res.removed}건 제거` : ''
          }`,
          'success'
        );
        return;
      }
      await updateSelectedRound(
        {
          ...selectedRound,
          budgetConfirmYn: 'N',
          budgetConfirmAt: null,
          budgetConfirmBy: null,
        },
        { persist: true }
      );
      cleared = await clearConfirmBalancesOnUnconfirm({
        tenantCode,
        fiscalYear: getValues('fiscalYear') || year,
        budgetKindCode: selectedRound.budgetKindCode,
      });
    } catch (e) {
      showAlert(e?.message || '예산확정 취소에 실패했습니다.', 'error');
      return;
    }
    showAlert(
      `예산확정이 취소되었습니다.${
        cleared.removed ? `\n현액 ${cleared.removed}건 제거` : ''
      }`,
      'success'
    );
  }, [selectedRound, updateSelectedRound, tenantCode, getValues, year, refreshFromStore]);
  const handleCreateBudget = useCallback(async () => {
    const tenant = tenantCode;
    const fy = getValues('fiscalYear') || year;
    if (selectedRound && selectedAdjNo != null && (selectedRound.adjStages || []).length) {
      const copyAdj = await showConfirm(
        '선택한 조정차수에 직전 조정차수 데이터를 복사할까요?\n(취소 시 전년도 예산차수 이관)',
        'warning'
      );
      if (copyAdj) {
        const synced = syncAdjGridEdits() || selectedRound;
        const result = copyPrevAdjToStage(synced, selectedAdjNo);
        if (!result.ok) {
          showAlert(result.reason, 'warning');
          return;
        }
        try {
          await updateSelectedRound(touchMod(result.round), { persist: true });
        } catch (e) {
          showAlert(e?.message || '복사에 실패했습니다.', 'error');
          return;
        }
        showAlert('이전 조정차수 데이터를 복사했습니다.', 'success');
        return;
      }
    }
    const ok = await showConfirm(
      `${Number(fy) - 1}년 예산차수를 ${fy}년으로 이관(예산생성) 할까요?`,
      'warning'
    );
    if (!ok) return;
    let result;
    try {
      result = await createBudgetFromPrevYear(tenant, fy, { userId: user?.id || '' });
    } catch (e) {
      showAlert(e?.message || '예산생성에 실패했습니다.', 'error');
      return;
    }
    if (!result.ok) {
      showAlert(result.reason, 'warning');
      return;
    }
    setRounds(result.rounds);
    const uid = result.created?.roundUid || result.rounds[0]?.roundUid || '';
    setSelectedRoundUid(uid);
    pushRoundGrid(result.rounds);
    pushAdjGridForList(result.rounds, uid);
    showAlert(
      result.fromSeed
        ? '전년 데이터가 없어 신규 본예산을 생성했습니다.'
        : '전년 예산차수를 이관했습니다.',
      'success'
    );
  }, [
    getValues,
    year,
    tenantCode,
    selectedRound,
    selectedAdjNo,
    syncAdjGridEdits,
    updateSelectedRound,
    user,
    pushRoundGrid,
    pushAdjGridForList,
  ]);

  const persistDeptRows = useCallback(
    async (deptRows, opts = {}) => {
      if (!selectedRound || selectedAdjNo == null) return;
      const cleaned = (deptRows || [])
        .filter(isActiveModRow)
        .map(({ mod, _tempId, submitAtDisp, receiveAtDisp, adjustAtDisp, ...rest }) => rest);
      const prevStage = selectedStage || {};
      const stage = syncStageAmounts({
        ...prevStage,
        deptRows: cleaned,
        mod: prevStage.mod === 'A' ? 'A' : 'U',
      });
      const stages = (selectedRound.adjStages || []).map((s) =>
        Number(s.adjNo) === Number(selectedAdjNo) ? stage : s
      );
      // 제출/접수/조정은 액션 즉시 반영 — F10 전에 팝업 재오픈해도 유지
      await updateSelectedRound(touchMod({ ...selectedRound, adjStages: stages }), {
        persist: true,
        ...opts,
      });
    },
    [selectedRound, selectedStage, selectedAdjNo, updateSelectedRound]
  );

  const applyDeptFlag = useCallback(
    async (flag, label) => {
      if (!selectedStage) return;
      if (adjLocked && !String(flag).includes('Cancel')) {
        showAlert('확정된 차수에서는 처리할 수 없습니다.', 'warning');
        return;
      }
      const rows = deptGridRef.current?.getData?.() || [];
      // 체크(전체선택 포함) 우선 — 포커스(선택) 행만 있으면 단건
      const checked = deptGridRef.current?.getCheckedData?.() || [];
      const focused = deptGridRef.current?.getSelectedRow?.();
      let targets = [];
      if (checked.length > 0) {
        targets = checked
          .filter((r) => r?.deptCode)
          .map((r) => ({ deptCode: r.deptCode, inoutDiv: r.inoutDiv || '지출' }));
      } else if (focused?.deptCode) {
        targets = [{ deptCode: focused.deptCode, inoutDiv: focused.inoutDiv || '지출' }];
      } else {
        showAlert('체크하거나 행을 선택한 뒤 처리하세요.', 'warning');
        return;
      }
      if (!targets.length) {
        showAlert('대상 부서가 없습니다.', 'warning');
        return;
      }

      // 토글: 접수/조정 — 대상이 모두 해당 Yn=Y 이면 취소 플래그로 전환
      let effectiveFlag = flag;
      let effectiveLabel = label;
      const isRequest = String(selectedStage.adjName || '').includes('요구');
      if (flag === 'receive' || flag === 'receiveCancel') {
        const allRecv = targets.every((t) => {
          const row = rows.find(
            (r) =>
              String(r.deptCode) === String(t.deptCode) &&
              (r.inoutDiv || '지출') === (t.inoutDiv || '지출')
          );
          return row?.receiveYn === 'Y';
        });
        if (flag === 'receive' && allRecv) {
          effectiveFlag = isRequest ? 'receiveCancel' : 'receiveCancelAndSubmit';
          effectiveLabel = '접수취소';
        } else if (flag === 'receive') {
          effectiveFlag = isRequest ? 'receive' : 'receiveAndSubmit';
          effectiveLabel = '접수';
        }
      } else if (flag === 'adjust' || flag === 'adjustCancel') {
        const allAdj = targets.every((t) => {
          const row = rows.find(
            (r) =>
              String(r.deptCode) === String(t.deptCode) &&
              (r.inoutDiv || '지출') === (t.inoutDiv || '지출')
          );
          return row?.adjustYn === 'Y';
        });
        if (flag === 'adjust' && allAdj) {
          effectiveFlag = 'adjustCancel';
          effectiveLabel = '조정취소';
        }
      } else if (flag === 'submit' || flag === 'submitCancel') {
        const allSub = targets.every((t) => {
          const row = rows.find(
            (r) =>
              String(r.deptCode) === String(t.deptCode) &&
              (r.inoutDiv || '지출') === (t.inoutDiv || '지출')
          );
          return row?.submitYn === 'Y';
        });
        if (flag === 'submit' && allSub) {
          effectiveFlag = 'submitCancel';
          effectiveLabel = '제출취소';
        }
      }

      const confirmMsg =
        checked.length > 0
          ? `체크한 ${targets.length}건에 「${effectiveLabel}」 처리할까요?`
          : `선택 행(${focused.inoutDiv || '지출'} ${focused.deptCode} ${focused.deptName || ''})에 「${effectiveLabel}」 처리할까요?`;
      const ok = await showConfirm(confirmMsg, 'warning');
      if (!ok) return;
      const stage = setDeptFlag(
        { ...selectedStage, deptRows: rows },
        targets,
        effectiveFlag,
        user?.id || user?.userId || ''
      );
      try {
        await persistDeptRows(stage.deptRows);
      } catch (e) {
        showAlert(e?.message || `「${effectiveLabel}」 처리에 실패했습니다.`, 'error');
        return;
      }
      deptGridRef.current?.setData?.(
        (stage.deptRows || []).map((d) => ({
          ...d,
          submitAtDisp: formatDts(d.submitAt),
          receiveAtDisp: formatDts(d.receiveAt),
          adjustAtDisp: formatDts(d.adjustAt),
        }))
      );
      deptGridRef.current?.clearRowChecks?.();
      showAlert(`「${effectiveLabel}」 ${targets.length}건 처리했습니다.`, 'success');
    },
    [selectedStage, adjLocked, persistDeptRows, user]
  );

  useRegisterGridExport({
    id: ROUND_GRID_ID,
    label: '예산차수',
    getData: () => roundGridRef.current?.getData?.() || [],
    getDataColumns: () => roundGridRef.current?.getDataColumns?.() || [],
    joinKeys: ['roundUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: ADJ_GRID_ID,
    label: '조정차수',
    getData: () => adjGridRef.current?.getData?.() || [],
    getDataColumns: () => adjGridRef.current?.getDataColumns?.() || [],
    joinKeys: ['adjNo'],
    order: 1,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '예산차수' });

  // 기관 변경 시 전년·최종예산 모드 옵션 동기
  useEffect(() => {
    setValue('priorYearBasis', getPriorYearBasis(tenantCode));
    setValue('finalBudgetMode', getFinalBudgetMode(tenantCode));
  }, [tenantCode, setValue]);

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        min: 2000,
        max: 2099,
        onChange: () => {
          setTimeout(() => {
            refreshFromStore().catch((e) => {
              showAlert(e?.message || '조회에 실패했습니다.', 'error');
            });
          }, 0);
        },
      },
      {
        name: 'finalBudgetMode',
        label: '최종모드',
        type: 'select',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        options: FINAL_BUDGET_MODE_OPTIONS,
        title:
          'sumDelta=본·추경·성립전·간주 증감합 / lastRound=마지막 확정차수 절대액(직전 전체 로드). 현액: 성립전 확정즉시·추경확정시 흡수, 간주는 구분 유지.',
        onChange: async (e) => {
          const v = e?.target?.value ?? getValues('finalBudgetMode');
          try {
            await setFinalBudgetMode(tenantCode, v);
          } catch (err) {
            showAlert(err?.message || '최종모드 저장에 실패했습니다.', 'error');
            return;
          }
          showAlert(
            v === FINAL_BUDGET_MODE.LAST_ROUND
              ? '최종예산 모드: lastRound · 직전차수 전체 불러와 절대액 · 성립전·간주 포함'
              : '최종예산 모드: sumDelta · 전차수 증감합 · 성립전·간주 포함 · GNK 기본',
            'success'
          );
        },
      },
      {
        name: 'priorYearBasis',
        label: '전년기준',
        type: 'select',
        width: CommonCss.w140,
        labelWidth: SEARCH_LABEL_W,
        options: PRIOR_YEAR_BASIS_OPTIONS,
        title:
          '본예산 비교 시 전년도 금액 기준 · yearFinal은 최종모드와 동일 규칙',
        onChange: async (e) => {
          const v = e?.target?.value ?? getValues('priorYearBasis');
          try {
            await setPriorYearBasis(tenantCode, v);
          } catch (err) {
            showAlert(err?.message || '전년 비교 기준 저장에 실패했습니다.', 'error');
            return;
          }
          showAlert(
            v === 'yearFinal'
              ? '전년 비교 기준: 전년 최종(최종모드 적용)'
              : '전년 비교 기준: 전년 본예산만',
            'success'
          );
        },
      },
    ],
  ];

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    {
      id: 'add',
      label: '추가',
      icon: '➕',
      onClick: handleActiveAdd,
      hotkey: 'F8',
      title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8) · 그리드 선택 Alt+F2',
    },
    {
      id: 'delete',
      label: '삭제',
      icon: '❌',
      onClick: handleActiveDelete,
      hotkey: 'F9',
      variant: 'danger',
      title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9) · 그리드 선택 Alt+F2',
    },
    { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    excelButton,
  ];

  const extraButtons = [
    {
      id: 'importBizPlan',
      label: '사업계획서 가져오기',
      onClick: handleImportBizPlan,
      title: '선택 예산차수의 지출예산편성요구에 사업계획서(최신 저장건) 반영',
    },
    { id: 'addPre', label: '성립전추가', onClick: handleAddPreRound },
    { id: 'adjConfirm', label: '조정확정', onClick: handleAdjConfirm },
    { id: 'adjUnconfirm', label: '조정확정취소', onClick: handleAdjUnconfirm },
    { id: 'budgetConfirm', label: '예산확정', onClick: handleBudgetConfirm, variant: 'primary' },
    { id: 'budgetUnconfirm', label: '예산확정취소', onClick: handleBudgetUnconfirm },
    { id: 'createBudget', label: '예산생성', onClick: handleCreateBudget },
    { id: 'deptPopup', label: '부서별 요구/조정상태', onClick: openDeptPopup },
  ];

  // 집계 조회 팝업 — 추가/삭제/저장·부서생성 없음 (제출·접수·조정만)
  const popupButtons = [
    { id: 'dSubmit', label: '제출', onClick: () => applyDeptFlag('submit', '제출'), title: '토글: 재클릭 시 제출취소' },
    { id: 'dRecv', label: '접수', onClick: () => applyDeptFlag('receive', '접수'), title: '토글: 재클릭 시 접수취소 · 조정단계는 제출도 함께 처리' },
    { id: 'dAdj', label: '조정', onClick: () => applyDeptFlag('adjust', '조정'), title: '토글: 재클릭 시 조정취소' },
  ];

  const fiscalYear = methods.watch('fiscalYear') || year;

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="budget-round-register"
          prefsUserId={prefsUserId}
          summary={`회계연도 ${fiscalYear}`}
        >
          <FormArea fields={searchFields} compact onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <div
        style={{
          fontSize: '0.75rem',
          color: '#6B7280',
          marginBottom: 4,
          flexShrink: 0,
        }}
      >
        활성 그리드: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 순환
        {activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}
        전년기준은 편성요구·예산서 공통 기관옵션
      </div>

      <Stack>
        <Panel>
          <ActiveGridHeader
            gridId={ROUND_GRID_ID}
            extra="성립전=끝자리 · [성립전추가] · 예산확정 시 잠금"
          >
            예산차수 (100 / 210·211·212…)
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={ROUND_GRID_ID}
              label="예산차수"
              order={0}
              onAdd={handleAddRound}
              onDelete={handleDeleteRound}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={ROUND_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={roundGridRef}
                  columns={roundColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === ROUND_GRID_ID}
                  {...roundGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="예산차수"
                  gridId={ROUND_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={roundGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel>
          <ActiveGridHeader
            gridId={ADJ_GRID_ID}
            extra="행 더블클릭 또는 [부서별 요구/조정상태] · F8/F9는 활성 그리드"
          >
            {`요구/조정차수${selectedRound ? ` · ${selectedRound.roundName || selectedRound.budgetClass}` : ''}`}
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={ADJ_GRID_ID}
              label="조정차수"
              order={1}
              onAdd={handleAddAdj}
              onDelete={handleDeleteAdj}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={ADJ_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={adjGridRef}
                  columns={adjColumns}
                  height="100%"
                  showRowNumber={false}
                  showModColumn
                  headerActive={activeGridId === ADJ_GRID_ID}
                  {...adjGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="조정차수"
                  gridId={ADJ_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={adjGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      {excelDialog}

      <DraggableDialog
        open={deptPopupOpen}
        onClose={() => setDeptPopupOpen(false)}
        keepMounted
        dialogWidth="min(1320px, 96vw)"
        dialogHeight="min(880px, 92vh)"
        title={
          [
            '부서별 편성 요구/조정 상태',
            selectedStage
              ? `${selectedRound?.roundName || ''} / ${selectedStage.adjName}`.trim()
              : '',
            adjLocked ? '[잠금]' : '',
          ]
            .filter(Boolean)
            .join(' — ')
        }
      >
        <ActionButtonGroup buttons={popupButtons} />
        <DialogGridArea>
          <HiGridComponent
            ref={deptGridRef}
            columns={deptColumns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            showModColumn={false}
            enableRowCheck
            enableHeaderMenu
            enableColumnFilters
            enableFreeze
            enableSelectionStats
            enableQuickExport
            exportFileName="부서별편성요구조정상태"
            gridId={DEPT_GRID_ID}
            prefsUserId={prefsUserId}
          />
        </DialogGridArea>
      </DraggableDialog>
    </PageContainer>
  );
};

const BudgetRoundRegister = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <BudgetRoundRegisterInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default BudgetRoundRegister;
